import { Component, OnInit } from '@angular/core';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TableModule } from "primeng/table";
import { ButtonModule } from 'primeng/button';
import { MessagingService } from '../../../../services/messaging.service';
import { MessagesState } from '../../../../state/messages/message-state';
import { Store } from '@ngxs/store';
import { ClientMessageHeader } from '../../../../models/client-message';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { AsyncPipe, DatePipe, CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { map, startWith, take } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-notification-manager',
  standalone: true,
  imports: [
    OverlayPanelModule,
    ButtonModule,
    TableModule,
    AsyncPipe,
    DatePipe,
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    CalendarModule
  ],
  templateUrl: './notification-manager.component.html',
  styleUrl: './notification-manager.component.scss',
})
export class NotificationManagerComponent implements OnInit {
    sidebarVisible: boolean = true;
    messages$ : Observable<ClientMessageHeader[]> = this._store.select(MessagesState.messages);
    selectedMessage$ = new BehaviorSubject<ClientMessageHeader | null>(null);

    // Filter controls
    readStatusFilter = new FormControl<'all' | 'read' | 'unread'>('all');
    titleFilter = new FormControl<string>('');
    startDateFilter = new FormControl<Date | null>(null);
    endDateFilter = new FormControl<Date | null>(null);

    // Filtered messages
    filteredMessages$: Observable<ClientMessageHeader[]>;

    readStatusOptions = [
        { label: 'All', value: 'all' },
        { label: 'Read', value: 'read' },
        { label: 'Unread', value: 'unread' }
    ];

    constructor
    (
        private _store: Store,
        private _messageService: MessagingService,
        private _route: ActivatedRoute
    )
    {
        this.filteredMessages$ = combineLatest([
            this.messages$,
            this.readStatusFilter.valueChanges.pipe(startWith('all')),
            this.titleFilter.valueChanges.pipe(startWith('')),
            this.startDateFilter.valueChanges.pipe(startWith(null)),
            this.endDateFilter.valueChanges.pipe(startWith(null))
        ]).pipe(
            map(([messages, readStatus, title, startDate, endDate]) => {
                return messages.filter((message: ClientMessageHeader) => {
                    if (this.selectedMessage$.value && message.id === this.selectedMessage$.value.id) return true;
                    if (readStatus === 'read' && !message.read) return false;
                    if (readStatus === 'unread' && message.read) return false;

                    if (title && !message.title.toLowerCase().includes(title.toLowerCase())) {
                        return false;
                    }

                    if (startDate) {
                        const messageDate = new Date(message.messageTime);
                        messageDate.setHours(0, 0, 0, 0);
                        const filterStart = new Date(startDate);
                        filterStart.setHours(0, 0, 0, 0);
                        if (messageDate < filterStart) return false;
                    }

                    if (endDate) {
                        const messageDate = new Date(message.messageTime);
                        messageDate.setHours(0, 0, 0, 0);
                        const filterEnd = new Date(endDate);
                        filterEnd.setHours(0, 0, 0, 0);
                        if (messageDate > filterEnd) return false;
                    }

                    return true;
                });
            })
        );
    }

    ngOnInit(): void
    {
        // Check for messageId query parameter and pre-select message if provided
        this._route.queryParams.pipe(take(1)).subscribe(params => {
            const messageId = params['messageId'];
            if (messageId) {
                // Wait for messages to be available, then find and select the message
                this.messages$.pipe(take(1)).subscribe(messages => {
                    const message = messages.find(m => m.id === parseInt(messageId, 10));
                    if (message) {
                        this.selectMessage(message);
                    }
                });
            }
        });
    }

    selectMessage(message: ClientMessageHeader) : void
    {
        this.selectedMessage$.next(message);

        if (!message.read) {
            this.markAsRead(message);
        }
    }

    markAsRead(message: ClientMessageHeader) : void
    {
        this._messageService.markMessageAsRead(message);
    }

    clearSelection(): void
    {
        this.selectedMessage$.next(null);
    }

    clearFilters(): void
    {
        this.readStatusFilter.setValue('all');
        this.titleFilter.setValue('');
        this.startDateFilter.setValue(null);
        this.endDateFilter.setValue(null);
    }
}